import { useReactiveVar } from '@apollo/client';
import Box from '@mui/material/Box';
import CheckBox from '@mui/material/Checkbox';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import type React from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import viewStateVar from 'src/store/viewState';
import styles from './TableStyles';

interface HeadCell {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
}

interface TableCellValue {
  value: ReactNode;
  display: boolean;
}

type TableRowData = TableCellValue[];

interface HeaderCheckboxConfig {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
}

interface TableComponentProps {
  checkboxVisibility?: boolean;
  minTableHeight?: number;
  rowsPerPage?: number;
  loading?: boolean;
  headCells?: HeadCell[];
  rows?: TableRowData[];
  selectMode?: boolean;
  onChangeSelect?: (row: TableRowData, event: ChangeEvent<HTMLInputElement>) => void;
  selectedRowIds?: Set<string>;
  headerCheckbox?: HeaderCheckboxConfig;
}

interface EnhancedTableHeadProps {
  order?: 'asc' | 'desc';
  orderBy?: string;
  headCells: HeadCell[];
  selectMode: boolean;
  headerCheckbox?: HeaderCheckboxConfig;
}

interface LoadingTableProps {
  headCells: HeadCell[];
  rowsPerPage: number;
}

export default function TableComponent({
  checkboxVisibility,
  minTableHeight,
  rowsPerPage = 10,
  loading = false,
  headCells = [],
  rows = [],
  selectMode = false,
  onChangeSelect = () => {},
  selectedRowIds,
  headerCheckbox,
}: TableComponentProps): React.ReactElement {
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);

  const handleCheckboxChange = (row: TableRowData) => (event: ChangeEvent<HTMLInputElement>) => {
    onChangeSelect(row, event);
  };

  return (
    <TableContainer style={{ maxHeight: minTableHeight }} className={classes.tableContainer}>
      <Table className={classes.tableWrapper} aria-labelledby="tableTitle">
        <EnhancedTableHead selectMode={selectMode} headCells={headCells} headerCheckbox={headerCheckbox} />

        <TableBody className={classes.tableBody}>
          {loading ? (
            <LoadingTable rowsPerPage={rowsPerPage} headCells={headCells} />
          ) : (
            rows.map((row, index) => {
              const rowKey = `row-${index}-${row[0]?.value ?? ''}`;
              return (
                <TableRow role="checkbox" tabIndex={-1} key={rowKey} className={classes.tableRow}>
                  {selectMode ? (
                    <TableCell padding="checkbox" align={'left'}>
                      {(checkboxVisibility ||
                        viewState.signedInUserRole === 'ADM' ||
                        !row.find((item) => item.value === 'Admin')) && (
                        <CheckBox
                          sx={{ color: 'text.primary', '&.Mui-checked': { color: 'primary.main' } }}
                          onChange={handleCheckboxChange(row)}
                          {...(selectedRowIds !== undefined
                            ? { checked: selectedRowIds.has(String(row[0]?.value ?? '')) }
                            : {})}
                        />
                      )}
                    </TableCell>
                  ) : null}
                  {row.map((v, cellIndex) => {
                    if (v.display)
                      return (
                        <TableCell
                          // biome-ignore lint/suspicious/noArrayIndexKey: table cells are dynamic with no stable identifier
                          key={`cell-${cellIndex}`}
                          className={classes.tableCell}
                          align={'left'}
                          padding={'normal'}
                        >
                          {v.value}
                        </TableCell>
                      );
                    return null;
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LoadingTable({ headCells, rowsPerPage }: LoadingTableProps): React.ReactElement {
  const classes = styles();
  return (
    <>
      {Array(rowsPerPage)
        .fill('0')
        .map((_, k) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no stable identifier
          <TableRow key={`loading-row-${k}`} className={classes.tableRow}>
            <TableCell padding="checkbox" align={'left'}>
              <Skeleton animation="wave" />
            </TableCell>
            {headCells.map((headCell) => (
              <TableCell
                key={`loading-cell-${headCell.id}`}
                className={classes.tableCell}
                align={'left'}
                padding={'normal'}
              >
                <Skeleton animation="wave" />
              </TableCell>
            ))}
          </TableRow>
        ))}
    </>
  );
}

function EnhancedTableHead({
  order,
  orderBy,
  headCells,
  selectMode,
  headerCheckbox,
}: EnhancedTableHeadProps): React.ReactElement {
  const classes = styles();

  return (
    <TableHead className={classes.tableHeader}>
      <TableRow>
        {selectMode ? (
          <TableCell className={classes.tableCell} align={'left'} padding="checkbox">
            {headerCheckbox ? (
              <CheckBox
                sx={{ color: 'text.primary', '&.Mui-checked': { color: 'primary.main' } }}
                checked={headerCheckbox.checked}
                indeterminate={headerCheckbox.indeterminate}
                disabled={headerCheckbox.disabled}
                onChange={headerCheckbox.onChange}
                inputProps={{ 'aria-label': headerCheckbox.ariaLabel ?? 'Select all' }}
              />
            ) : null}
          </TableCell>
        ) : null}
        {headCells.map((headCell) => (
          <TableCell
            className={classes.tableCell}
            key={headCell.id}
            align={'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.label}
            {orderBy === headCell.id ? (
              <Box component="span" sx={visuallyHidden}>
                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
              </Box>
            ) : null}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
